import React, { Component } from "react";

class Conversation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      wsStatus: "disconnected",
      error: "",
      isRecording: false,

      // ✅ NEW
      sessionId: "",
      draft: null,
      assistantText: "", // optional if your server sends {type:"text", text:"..."}
    };

    // WS
    this.ws = null;

    // Audio capture (mic)
    this.mediaStream = null;
    this.audioCtx = null;
    this.sourceNode = null;
    this.processorNode = null;
    this.micAnalyser = null;

    // Playback (assistant)
    this.playCtx = null;
    this.playQueue = [];
    this.isPlaying = false;
    this.outAnalyser = null;
    this.outGain = null;

    // Visualiser
    this.circleCanvasRef = React.createRef(); // assistant
    this.lineCanvasRef = React.createRef(); // mic
    this.animId = null;

    // Sensitivity + smoothing
    this.micLevel = 0;
    this.outLevel = 0;

    this.micAutoGain = 1.0;
    this.outAutoGain = 1.0;

    // Auto-commit on pause (silence)
    this._autoCommitTimer = null;
    this._lastVoiceAt = 0;
    this._lastCommitAt = 0;

    // Tuning knobs
    this.SILENCE_THRESHOLD = 0.015;   // RMS threshold (try 0.012–0.03)
    this.SILENCE_MS = 900;            // pause length to trigger commit
    this.MIN_GAP_BETWEEN_COMMITS_MS = 1200; // avoid rapid repeats

  }

  componentWillUnmount() {
    this.stopRecording();
    this.disconnectWs();
    this.cleanupPlayback();
    this.stopVisualizer();
  }

  
  // --------------------------
  // Helper
  // --------------------------
  computeRms = (float32Array) => {
    let sum = 0;
    for (let i = 0; i < float32Array.length; i++) {
      const v = float32Array[i];
      sum += v * v;
    }
    return Math.sqrt(sum / float32Array.length);
  };

  scheduleAutoCommit = () => {
    if (this._autoCommitTimer) clearTimeout(this._autoCommitTimer);

    this._autoCommitTimer = setTimeout(() => {
      const now = Date.now();
      const sinceVoice = now - this._lastVoiceAt;
      const sinceCommit = now - this._lastCommitAt;

      // Only commit if we're still recording, connected, and it's been quiet long enough
      if (!this.state.isRecording) return;
      if (this.state.wsStatus !== "connected") return;

      console.log("[client] auto-commit check", { sinceVoice, sinceCommit, ws: this.state.wsStatus });

      if (!this._lastVoiceAt) {
        // console.log("[client] skip auto-commit (no voice yet)");
        return;
      }

      if (sinceVoice >= this.SILENCE_MS && sinceCommit >= this.MIN_GAP_BETWEEN_COMMITS_MS) {
        // console.log("[client] auto-commit (pause detected)");
        // console.log("[client] ✅ sending commit");
        this.sendWs({ type: "commit" });
        this._lastCommitAt = now;
      }
    }, this.SILENCE_MS + 50); // small buffer
  };

  clearAutoCommit = () => {
    if (this._autoCommitTimer) clearTimeout(this._autoCommitTimer);
    this._autoCommitTimer = null;
  };


  // --------------------------
  // WebSocket
  // --------------------------
  connectWs = () => {
    try {
      if (this.state.wsStatus === "connected" || this.state.wsStatus === "connecting") return;

      this.setState({ wsStatus: "connecting", error: "" });

      const ws = new WebSocket("ws://localhost:4000");

      ws.onopen = () => {
        this.ws = ws;
        this.setState({ wsStatus: "connected" });
        this.sendWs({ type: "start" });
      };

      ws.onclose = () => {
        this.ws = null;
        this.setState({ wsStatus: "disconnected", isRecording: false });
      };

      ws.onerror = () => {
        this.setState({ error: "WebSocket error (check server is running)" });
      };

      ws.onmessage = (ev) => {
        let msg = null;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }

        // ✅ server sends {type:"ready", sessionId}
        if (msg.type === "ready") {
          const sessionId = msg.sessionId || "";
          this.setState({ sessionId });

          // ✅ ask server for current draft snapshot (optional but handy)
          this.sendWs({ type: "get_history" });
          return;
        }

        // ✅ LIVE draft updates from noteTaker
        if (msg.type === "history_update" && msg.draft) {
          this.setState({ draft: msg.draft });
          return;
        }

        // ✅ Snapshot response
        if (msg.type === "history_snapshot") {
          this.setState({ draft: msg.draft || null });
          return;
        }

        // Optional assistant text deltas (if server forwards them)
        if (msg.type === "text" && msg.text) {
          this.setState((prev) => ({ assistantText: prev.assistantText + msg.text }));
          return;
        }

        // Audio messages
        if (msg.type === "audio" && msg.audio_b64) {
          this.enqueuePcmAudio(msg.audio_b64);
          return;
        }

        // Compatibility (older raw event shapes)
        if (msg.type === "response.audio.delta" && msg.delta) {
          this.enqueuePcmAudio(msg.delta);
          return;
        }
      };

      this.ws = ws;
    } catch (e) {
      this.setState({ wsStatus: "disconnected", error: e.message || "Failed to connect" });
    }
  };

  disconnectWs = () => {
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }
    this.setState({ wsStatus: "disconnected" });
  };

  sendWs = (obj) => {
    if (!this.ws || this.ws.readyState !== 1) return;
    this.ws.send(JSON.stringify(obj));
  };

  // --------------------------
  // Recording + streaming (mic)
  // --------------------------
  startRecording = async () => {
    if (this.state.wsStatus !== "connected") {
      this.connectWs();
      setTimeout(() => {
        if (this.state.wsStatus !== "connected") {
          this.setState({ error: "Connecting… try again in a moment." });
        }
      }, 200);
      return;
    }

    try {
      this.setState({ error: "" });

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      this.sourceNode = this.audioCtx.createMediaStreamSource(this.mediaStream);

      // Mic analyser (linear waveform)
      this.micAnalyser = this.audioCtx.createAnalyser();
      this.micAnalyser.fftSize = 2048;
      this.micAnalyser.smoothingTimeConstant = 0.85;

      // Processor (PCM streaming)
      const bufferSize = 4096;
      this.processorNode = this.audioCtx.createScriptProcessor(bufferSize, 1, 1);

     this.processorNode.onaudioprocess = (e) => {
        if (!this.state.isRecording) return;
        const input = e.inputBuffer.getChannelData(0);

        // ---- 1) pause detection (RMS) ----
        const rms = this.computeRms(input);

        if (!this._rmsLogAt || Date.now() - this._rmsLogAt > 500) {
          // console.log("[client] rms", rms.toFixed(4), "recording=", this.state.isRecording);
          this._rmsLogAt = Date.now();
        }

        // Voice detected
        if (rms > this.SILENCE_THRESHOLD) {
          this._lastVoiceAt = Date.now();
          this.scheduleAutoCommit(); // (re)arm commit for after silence
        } else {
          // still arm it, so if user stops speaking we commit after SILENCE_MS
          this.scheduleAutoCommit();
        }

        // ---- 2) streaming audio as before ----
        const pcm16 = this.floatTo16BitPCM(
          this.downsampleBuffer(input, this.audioCtx.sampleRate, 24000)
        );
        const b64 = this.arrayBufferToBase64(pcm16.buffer);
        this.sendWs({ type: "audio", audio_b64: b64 });
      };


      // graph: source -> analyser -> processor -> destination
      this.sourceNode.connect(this.micAnalyser);
      this.micAnalyser.connect(this.processorNode);
      this.processorNode.connect(this.audioCtx.destination);

      this.setState({ isRecording: true });
      this.startVisualizer();
    } catch (e) {
      this.setState({ error: e.message || "Failed to start recording" });
      this.stopRecording();
    }
  };

  stopRecording = () => {
    try {
      if (this.state.isRecording) {
        this.setState({ isRecording: false });

        if (this.state.wsStatus === "connected") {
          this.sendWs({ type: "commit" });
          this._lastCommitAt = Date.now();
        }
      }

      if (this.processorNode) {
        this.processorNode.disconnect();
        this.processorNode.onaudioprocess = null;
      }
      if (this.micAnalyser) this.micAnalyser.disconnect();
      if (this.sourceNode) this.sourceNode.disconnect();

      if (this.audioCtx) this.audioCtx.close();

      this.processorNode = null;
      this.micAnalyser = null;
      this.sourceNode = null;
      this.audioCtx = null;

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
      }
      this.mediaStream = null;
      this.clearAutoCommit();
      this._lastCommitAt = Date.now();

    } catch {}
  };

  toggleRecording = async () => {
    if (this.state.isRecording) this.stopRecording();
    else await this.startRecording();
  };

  // --------------------------
  // Playback (assistant) with analyser tap
  // --------------------------
  ensurePlayCtx = async () => {
    if (!this.playCtx) {
      this.playCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });

      // Tap assistant output through gain -> analyser -> destination
      this.outGain = this.playCtx.createGain();
      this.outGain.gain.value = 0.95;

      this.outAnalyser = this.playCtx.createAnalyser();
      this.outAnalyser.fftSize = 1024;
      this.outAnalyser.smoothingTimeConstant = 0.88;

      this.outGain.connect(this.outAnalyser);
      this.outAnalyser.connect(this.playCtx.destination);
    }

    if (this.playCtx.state === "suspended") {
      await this.playCtx.resume();
    }
  };

  enqueuePcmAudio = async (b64) => {
    await this.ensurePlayCtx();
    this.playQueue.push(b64);
    if (!this.isPlaying) this.playNextChunk();
    if (!this.animId) this.startVisualizer();
  };

  playNextChunk = async () => {
    if (!this.playCtx || this.playQueue.length === 0) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const b64 = this.playQueue.shift();
    const ab = this.base64ToArrayBuffer(b64);
    const int16 = new Int16Array(ab);

    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;

    const buffer = this.playCtx.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32, 0);

    const src = this.playCtx.createBufferSource();
    src.buffer = buffer;

    src.connect(this.outGain);

    src.onended = () => {
      this.playNextChunk();
      if (this.playQueue.length === 0) {
        if (!this.state.isRecording) this.maybeStopVisualizerSoon();
      }
    };

    src.start();
  };

  cleanupPlayback = () => {
    this.playQueue = [];
    this.isPlaying = false;

    if (this.playCtx) {
      try {
        this.playCtx.close();
      } catch {}
    }
    this.playCtx = null;
    this.outAnalyser = null;
    this.outGain = null;
  };

  // --------------------------
  // Visualiser (unchanged)
  // --------------------------
  startVisualizer = () => {
    const circleCanvas = this.circleCanvasRef.current;
    const lineCanvas = this.lineCanvasRef.current;
    if (!circleCanvas || !lineCanvas) return;

    const circleCtx = circleCanvas.getContext("2d");
    const lineCtx = lineCanvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const circleSize = Math.min(360, Math.floor(window.innerWidth * 0.7));
      circleCanvas.style.width = `${circleSize}px`;
      circleCanvas.style.height = `${circleSize}px`;
      circleCanvas.width = Math.floor(circleSize * dpr);
      circleCanvas.height = Math.floor(circleSize * dpr);

      const lineW = Math.min(520, Math.floor(window.innerWidth * 0.9));
      const lineH = 70;
      lineCanvas.style.width = `${lineW}px`;
      lineCanvas.style.height = `${lineH}px`;
      lineCanvas.width = Math.floor(lineW * dpr);
      lineCanvas.height = Math.floor(lineH * dpr);

      circleCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      lineCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);
    this._vizResize = resize;

    const micBuf = new Uint8Array(this.micAnalyser ? this.micAnalyser.fftSize : 2048);
    const outBuf = new Uint8Array(this.outAnalyser ? this.outAnalyser.fftSize : 1024);

    const draw = () => {
      // ---- MIC LINEAR WAVE ----
      const lw = lineCanvas.clientWidth;
      const lh = lineCanvas.clientHeight;

      lineCtx.clearRect(0, 0, lw, lh);

      lineCtx.fillStyle = "rgba(2,6,23,0.35)";
      lineCtx.fillRect(0, 0, lw, lh);

      let micLevel = 0;
      if (this.micAnalyser) {
        this.micAnalyser.getByteTimeDomainData(micBuf);

        let sum = 0;
        for (let i = 0; i < micBuf.length; i++) {
          const v = (micBuf[i] - 128) / 128;
          sum += v * v;
        }
        micLevel = Math.sqrt(sum / micBuf.length);

        this.micAutoGain = this.clamp(
          this.micAutoGain * 0.98 + (micLevel > 0.06 ? 0.75 : 1.15) * 0.02,
          0.6,
          2.8
        );
        const scaled = micLevel * this.micAutoGain;
        this.micLevel = this.micLevel * 0.85 + scaled * 0.15;

        lineCtx.lineWidth = 2.5;
        lineCtx.strokeStyle = this.state.isRecording
          ? "rgba(16,185,129,0.95)"
          : "rgba(148,163,184,0.75)";
        lineCtx.beginPath();

        for (let x = 0; x < lw; x++) {
          const idx = Math.floor((x / lw) * (micBuf.length - 1));
          const v = (micBuf[idx] - 128) / 128;
          const y = lh / 2 + v * (lh * 0.34);
          if (x === 0) lineCtx.moveTo(x, y);
          else lineCtx.lineTo(x, y);
        }

        lineCtx.stroke();
      } else {
        lineCtx.strokeStyle = "rgba(148,163,184,0.35)";
        lineCtx.beginPath();
        lineCtx.moveTo(0, lh / 2);
        lineCtx.lineTo(lw, lh / 2);
        lineCtx.stroke();
        this.micLevel *= 0.92;
      }

      // ---- ASSISTANT CIRCULAR WAVE ----
      const cw = circleCanvas.clientWidth;
      const ch = circleCanvas.clientHeight;
      const cx = cw / 2;
      const cy = ch / 2;

      circleCtx.clearRect(0, 0, cw, ch);

      circleCtx.fillStyle = "rgba(2,6,23,0.35)";
      circleCtx.beginPath();
      circleCtx.arc(cx, cy, Math.min(cw, ch) / 2, 0, Math.PI * 2);
      circleCtx.fill();

      let outLevel = 0;
      if (this.outAnalyser) {
        this.outAnalyser.getByteTimeDomainData(outBuf);

        let sum = 0;
        for (let i = 0; i < outBuf.length; i++) {
          const v = (outBuf[i] - 128) / 128;
          sum += v * v;
        }
        outLevel = Math.sqrt(sum / outBuf.length);

        this.outAutoGain = this.clamp(
          this.outAutoGain * 0.985 + (outLevel > 0.035 ? 0.85 : 1.25) * 0.015,
          0.8,
          5.5
        );
        const scaled = outLevel * this.outAutoGain;
        this.outLevel = this.outLevel * 0.82 + scaled * 0.18;
      } else {
        this.outLevel *= 0.92;
      }

      const baseR = Math.min(cw, ch) * 0.28;
      const pulse = this.clamp(this.outLevel * 3.6, 0, 1);
      const ringR = baseR + pulse * 28;

      circleCtx.beginPath();
      circleCtx.arc(cx, cy, ringR, 0, Math.PI * 2);
      circleCtx.lineWidth = 12;
      circleCtx.strokeStyle = this.isPlaying ? "rgba(59,130,246,0.25)" : "rgba(148,163,184,0.20)";
      circleCtx.stroke();

      const points = 220;
      circleCtx.beginPath();

      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * Math.PI * 2;

        let v = 0;
        if (this.outAnalyser) {
          const idx = Math.floor((i / points) * (outBuf.length - 1));
          v = (outBuf[idx] - 128) / 128;
        }

        const amp = v * (26 + pulse * 22);
        const r = ringR + amp;

        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;

        if (i === 0) circleCtx.moveTo(x, y);
        else circleCtx.lineTo(x, y);
      }

      circleCtx.closePath();
      circleCtx.lineWidth = 3.2;
      circleCtx.strokeStyle = this.isPlaying ? "rgba(59,130,246,0.95)" : "rgba(148,163,184,0.75)";
      circleCtx.stroke();

      this.animId = requestAnimationFrame(draw);
    };

    if (!this.animId) draw();
  };

  stopVisualizer = () => {
    if (this.animId) cancelAnimationFrame(this.animId);
    this.animId = null;

    if (this._vizResize) {
      window.removeEventListener("resize", this._vizResize);
      this._vizResize = null;
    }

    const c = this.circleCanvasRef.current;
    const l = this.lineCanvasRef.current;
    if (c) {
      const ctx = c.getContext("2d");
      ctx && ctx.clearRect(0, 0, c.width, c.height);
    }
    if (l) {
      const ctx = l.getContext("2d");
      ctx && ctx.clearRect(0, 0, l.width, l.height);
    }
  };

  maybeStopVisualizerSoon = () => {
    setTimeout(() => {
      const nothingHappening =
        !this.state.isRecording && !this.isPlaying && this.playQueue.length === 0;
      if (nothingHappening) this.stopVisualizer();
    }, 800);
  };

  // --------------------------
  // Audio helpers
  // --------------------------
  downsampleBuffer = (buffer, sampleRate, outRate) => {
    if (outRate === sampleRate) return buffer;
    if (outRate > sampleRate) return buffer;

    const ratio = sampleRate / outRate;
    const newLen = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLen);

    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
      let sum = 0;
      let count = 0;

      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        sum += buffer[i];
        count++;
      }

      result[offsetResult] = sum / count;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  };

  floatTo16BitPCM = (float32Array) => {
    const out = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      let s = Math.max(-1, Math.min(1, float32Array[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  };

  arrayBufferToBase64 = (arrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(arrayBuffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  base64ToArrayBuffer = (b64) => {
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  };

  clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  // --------------------------
  // Note rendering helpers
  // --------------------------
  renderKV = (label, value) => {
    if (!value) return null;
    return (
      <div className="text-sm">
        <div className="text-slate-400">{label}</div>
        <div className="text-slate-100 whitespace-pre-wrap">{String(value)}</div>
      </div>
    );
  };

  renderList = (label, arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return null;
    return (
      <div className="text-sm">
        <div className="text-slate-400">{label}</div>
        <ul className="mt-1 list-disc list-inside space-y-0.5 text-slate-100">
          {arr.map((x, i) => (
            <li key={`${label}-${i}`}>{String(x)}</li>
          ))}
        </ul>
      </div>
    );
  };

  renderNotePanel = () => {
    const d = this.state.draft;
    if (!d) {
      return (
        <div className="rounded-3xl border border-slate-800 bg-slate-900/40 shadow-xl p-6">
          <div className="text-sm text-slate-300">Mental Health Care Plan Draft</div>
          <div className="mt-3 text-sm text-slate-400">
            No notes yet. Start the interview and the notes will appear here in real time.
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-3xl border border-slate-800 bg-slate-900/40 shadow-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-300">MHCP Draft</div>
            <div className="mt-1 text-xs text-slate-500">
              Updated: {d?.meta?.updatedAt ? String(d.meta.updatedAt) : "—"}
            </div>
          </div>

          <button
            onClick={() => this.sendWs({ type: "get_history" })}
            className="text-xs text-slate-400 hover:text-slate-200"
            title="Refresh from server"
          >
            Refresh
          </button>
        </div>

        <div className="mt-5 space-y-5">
          {/* Patient + consent */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Patient</div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              {this.renderKV("Name", d?.patient?.fullName)}
              {this.renderKV("DOB", d?.patient?.dob)}
              {this.renderKV("Phone", d?.patient?.phone)}
              {this.renderKV("Consent", d?.consent?.given ? "Yes" : d?.consent?.given === false ? "No" : "")}
              {this.renderKV("Consent notes", d?.consent?.notes)}
            </div>
          </div>

          {/* Presenting issue */}
          {d.presentingIssue ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">Presenting issue</div>
              <div className="mt-3 text-sm text-slate-100 whitespace-pre-wrap">{d.presentingIssue}</div>
            </div>
          ) : null}

          {/* Mood / Anxiety / Sleep */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Symptoms</div>
            <div className="mt-3 space-y-4">
              <div>
                <div className="text-sm font-semibold text-slate-200">Mood</div>
                <div className="mt-2 space-y-2">
                  {this.renderList("Symptoms", d?.mood?.symptoms)}
                  {this.renderKV("Duration", d?.mood?.duration)}
                  {this.renderKV("Severity", d?.mood?.severity)}
                  {this.renderKV("Notes", d?.mood?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">Anxiety</div>
                <div className="mt-2 space-y-2">
                  {this.renderList("Symptoms", d?.anxiety?.symptoms)}
                  {this.renderKV("Triggers", d?.anxiety?.triggers)}
                  {this.renderKV("Panic", d?.anxiety?.panic)}
                  {this.renderKV("Notes", d?.anxiety?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">Sleep</div>
                <div className="mt-2 space-y-2">
                  {this.renderKV("Onset", d?.sleep?.onset)}
                  {this.renderKV("Maintenance", d?.sleep?.maintenance)}
                  {this.renderKV("Hours", d?.sleep?.hours)}
                  {this.renderKV("Nightmares", d?.sleep?.nightmares)}
                  {this.renderKV("Notes", d?.sleep?.notes)}
                </div>
              </div>
            </div>
          </div>

          {/* Trauma */}
          {(d?.trauma?.summary || d?.childhoodTrauma?.summary) ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">Trauma</div>

              {d?.trauma?.summary ? (
                <div className="mt-3 space-y-2">
                  <div className="text-sm font-semibold text-slate-200">Trauma history</div>
                  {this.renderKV("Summary", d?.trauma?.summary)}
                  {this.renderKV("Triggers", d?.trauma?.triggers)}
                  {this.renderKV("Flashbacks", d?.trauma?.flashbacks)}
                  {this.renderKV("Nightmares", d?.trauma?.nightmares)}
                  {this.renderKV("Notes", d?.trauma?.notes)}
                </div>
              ) : null}

              {d?.childhoodTrauma?.summary ? (
                <div className="mt-4 space-y-2">
                  <div className="text-sm font-semibold text-slate-200">Childhood trauma</div>
                  {this.renderKV("Summary", d?.childhoodTrauma?.summary)}
                  {this.renderKV("Age range", d?.childhoodTrauma?.ageRange)}
                  {this.renderList("Types", d?.childhoodTrauma?.types)}
                  {this.renderKV("Impact now", d?.childhoodTrauma?.currentImpact)}
                  {this.renderKV("Safety now", d?.childhoodTrauma?.safetyNow)}
                  {this.renderKV("Notes", d?.childhoodTrauma?.notes)}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Neurodevelopmental + ABI */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Neurodevelopment / ABI</div>
            <div className="mt-3 space-y-4">
              <div>
                <div className="text-sm font-semibold text-slate-200">Autism traits</div>
                <div className="mt-2 space-y-2">
                  {this.renderList("Traits", d?.neurodevelopmental?.autism?.traits)}
                  {this.renderKV("Sensory", d?.neurodevelopmental?.autism?.sensory)}
                  {this.renderKV("Routines", d?.neurodevelopmental?.autism?.routines)}
                  {this.renderKV("Social communication", d?.neurodevelopmental?.autism?.socialCommunication)}
                  {this.renderKV("Notes", d?.neurodevelopmental?.autism?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">ADHD traits</div>
                <div className="mt-2 space-y-2">
                  {this.renderList("Traits", d?.neurodevelopmental?.adhd?.traits)}
                  {this.renderKV("Onset", d?.neurodevelopmental?.adhd?.onset)}
                  {this.renderKV("Inattention", d?.neurodevelopmental?.adhd?.inattention)}
                  {this.renderKV("Hyperactivity/impulsivity", d?.neurodevelopmental?.adhd?.hyperactivityImpulsivity)}
                  {this.renderKV("Impairments", d?.neurodevelopmental?.adhd?.impairments)}
                  {this.renderKV("Notes", d?.neurodevelopmental?.adhd?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">Learning difficulties</div>
                <div className="mt-2 space-y-2">
                  {this.renderKV("Concerns", d?.neurodevelopmental?.learningDifficulties?.concerns)}
                  {this.renderKV("School history", d?.neurodevelopmental?.learningDifficulties?.schoolHistory)}
                  {this.renderKV("Diagnoses (patient-reported)", d?.neurodevelopmental?.learningDifficulties?.diagnoses)}
                  {this.renderKV("Supports", d?.neurodevelopmental?.learningDifficulties?.supports)}
                  {this.renderKV("Notes", d?.neurodevelopmental?.learningDifficulties?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">Acquired brain injury</div>
                <div className="mt-2 space-y-2">
                  {this.renderKV("History", d?.abi?.history)}
                  {this.renderKV("When", d?.abi?.when)}
                  {this.renderKV("Cause", d?.abi?.cause)}
                  {this.renderKV("Residuals", d?.abi?.residuals)}
                  {this.renderKV("Seizures", d?.abi?.seizures)}
                  {this.renderKV("Notes", d?.abi?.notes)}
                </div>
              </div>
            </div>
          </div>

          {/* Substance use */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Substance use</div>
            <div className="mt-3 space-y-4">
              <div>
                <div className="text-sm font-semibold text-slate-200">Alcohol</div>
                <div className="mt-2 space-y-2">
                  {this.renderKV("Pattern", d?.substanceHistory?.alcohol?.pattern)}
                  {this.renderKV("Quantity", d?.substanceHistory?.alcohol?.quantity)}
                  {this.renderKV("Frequency", d?.substanceHistory?.alcohol?.frequency)}
                  {this.renderKV("Last use", d?.substanceHistory?.alcohol?.lastUse)}
                  {this.renderKV("Withdrawal", d?.substanceHistory?.alcohol?.withdrawal)}
                  {this.renderKV("Impact", d?.substanceHistory?.alcohol?.impact)}
                  {this.renderKV("Notes", d?.substanceHistory?.alcohol?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">Nicotine</div>
                <div className="mt-2 space-y-2">
                  {this.renderKV("Pattern", d?.substanceHistory?.nicotine?.pattern)}
                  {this.renderKV("Quantity", d?.substanceHistory?.nicotine?.quantity)}
                  {this.renderKV("Notes", d?.substanceHistory?.nicotine?.notes)}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-200">Cannabis</div>
                <div className="mt-2 space-y-2">
                  {this.renderKV("Pattern", d?.substanceHistory?.cannabis?.pattern)}
                  {this.renderKV("Quantity", d?.substanceHistory?.cannabis?.quantity)}
                  {this.renderKV("Frequency", d?.substanceHistory?.cannabis?.frequency)}
                  {this.renderKV("Last use", d?.substanceHistory?.cannabis?.lastUse)}
                  {this.renderKV("Notes", d?.substanceHistory?.cannabis?.notes)}
                </div>
              </div>

              {Array.isArray(d?.substanceHistory?.other) && d.substanceHistory.other.length > 0 ? (
                <div>
                  <div className="text-sm font-semibold text-slate-200">Other substances</div>
                  <div className="mt-2 space-y-2">
                    {d.substanceHistory.other.map((x, i) => (
                      <div key={`sub-${i}`} className="rounded-xl border border-slate-800 bg-slate-900/30 p-3">
                        <div className="text-sm text-slate-200 font-semibold">{x.substance}</div>
                        <div className="mt-2 space-y-1 text-sm text-slate-100">
                          {x.pattern ? <div><span className="text-slate-400">Pattern: </span>{x.pattern}</div> : null}
                          {x.frequency ? <div><span className="text-slate-400">Frequency: </span>{x.frequency}</div> : null}
                          {x.lastUse ? <div><span className="text-slate-400">Last use: </span>{x.lastUse}</div> : null}
                          {x.notes ? <div className="whitespace-pre-wrap"><span className="text-slate-400">Notes: </span>{x.notes}</div> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Psychosis + Risk */}
          {(d?.psychosis || d?.risk) ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">Psychosis / Risk</div>

              <div className="mt-3 space-y-4">
                <div>
                  <div className="text-sm font-semibold text-slate-200">Psychosis screen</div>
                  <div className="mt-2 space-y-2">
                    {this.renderKV("Hallucinations", d?.psychosis?.hallucinations)}
                    {this.renderKV("Voices", d?.psychosis?.voices)}
                    {this.renderKV("Paranoia", d?.psychosis?.paranoia)}
                    {this.renderKV("Delusions", d?.psychosis?.delusions)}
                    {this.renderKV("Notes", d?.psychosis?.notes)}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-200">Risk</div>
                    {d?.risk?.clinicianAlertNeeded ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-rose-600/20 text-rose-200 border border-rose-800">
                        Clinician alert
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-2 space-y-2">
                    {this.renderKV("Suicidal thoughts", d?.risk?.suicidalThoughts)}
                    {this.renderKV("Plan", d?.risk?.plan)}
                    {this.renderKV("Means", d?.risk?.means)}
                    {this.renderKV("Intent", d?.risk?.intent)}
                    {this.renderKV("Past attempts", d?.risk?.pastAttempts)}
                    {this.renderKV("Self-harm", d?.risk?.selfHarm)}
                    {this.renderKV("Harmful thoughts/behaviours", d?.risk?.harmfulThoughtsOrBehaviours)}
                    {this.renderKV("Protective factors", d?.risk?.protectiveFactors)}
                    {this.renderKV("Supports", d?.risk?.supports)}
                    {this.renderKV("Notes", d?.risk?.notes)}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Optional: running summary */}
          {d?.summary ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">Summary</div>
              <div className="mt-3 text-sm text-slate-100 whitespace-pre-wrap">{d.summary}</div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // --------------------------
  // UI
  // --------------------------
  getButtonLabel = () => {
    if (this.state.isRecording) return "Stop";
    if (this.state.wsStatus === "connecting") return "Connecting…";
    return "Start";
  };

  render() {
    const { wsStatus, isRecording, error, sessionId } = this.state;

    const connected = wsStatus === "connected";
    const connecting = wsStatus === "connecting";

    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="w-full max-w-6xl px-6">
          {/* ✅ Two-column layout */}
          <div className="grid grid-cols-2 lg:grid-cols-2 xs:grid-cols-1 sm:grid-cols-1 gap-6 items-start">
            {/* LEFT: your existing audio UI */}
            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 shadow-xl p-8">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-300">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={[
                        "inline-block h-2.5 w-2.5 rounded-full",
                        isRecording
                          ? "bg-emerald-400"
                          : connected
                          ? "bg-sky-400"
                          : connecting
                          ? "bg-amber-400"
                          : "bg-slate-500",
                      ].join(" ")}
                    />
                    {isRecording ? "Recording" : wsStatus}
                  </span>
                </div>

                <button
                  onClick={() => {
                    if (!connected && !connecting) this.connectWs();
                    if (connected) this.sendWs({ type: "get_history" });
                  }}
                  className="text-xs text-slate-400 hover:text-slate-200"
                  title="Reconnect / Refresh notes"
                >
                  {connected ? "Online" : "Click here to connect"}
                </button>
              </div>

              {sessionId ? (
                <div className="mt-2 text-xs text-slate-500">
                  Session: <span className="text-slate-300">{sessionId}</span>
                </div>
              ) : null}

              {/* Mic linear wave */}
              <div className="mt-6 flex justify-center">
                <canvas ref={this.lineCanvasRef} className="rounded-2xl" aria-label="Microphone waveform" />
              </div>

              {/* Assistant circular wave */}
              <div className="mt-8 flex items-center justify-center">
                <canvas ref={this.circleCanvasRef} className="rounded-full" aria-label="Assistant waveform" />
              </div>

              {error ? (
                <div className="mt-6 rounded-2xl border border-rose-900/50 bg-rose-950/40 p-3 text-rose-200 text-sm">
                  {error}
                </div>
              ) : null}

              <div className="mt-10 flex justify-center">
                <button
                  onClick={this.toggleRecording}
                  disabled={connecting}
                  className={[
                    "rounded-lg w-52 shadow-xl px-10 py-4 text-lg font-semibold tracking-wide",
                    isRecording ? "bg-rose-600 hover:bg-rose-500" : "bg-emerald-600 hover:bg-emerald-500",
                    connecting ? "opacity-60 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  {this.getButtonLabel()}
                </button>
              </div>

              <div className="mt-6 text-center text-xs text-slate-400">
                Start to talk. Stop to send. The centre ring animates when the assistant speaks.
              </div>
            </div>

            {/* RIGHT: MHCP notes panel */}
            <div className="lg:sticky lg:top-6">
              {this.renderNotePanel()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Conversation;
